import React, {
  useEffect,
  useCallback,
  useState,
  useRef,
  useMemo
} from "react";
import { Link, useParams } from "react-router-dom";
import Information from "./information/Information";
import Details from "./details/Details";
import Invoicelog from "./invoicelog/Invoicelog";
import Fee from "./fee/Fee";
import Payments from "./payments/Payments";
import FeeCalculator from "./feecalculator/FeeCalculator";
import { showToast } from "../../utils/utils";
import { FtNo, responseStatus } from "../../locales/en";
import XeroWriteBack from "./xerowriteback";
import "./../../assets/scss/datepicker.scss";
import "./invoicestyle.scss";
import VoidedInvoiceInfoModal from "../../components/modals/VoidedInvoiceInfoModal";
import { INVOICE_STATUS_VOIDED } from "../../utils/constants/appConstants";
import { getQuoteStatusURL } from "../../utils/constants/apiEndPoints";
import privateAxios from "../../utils/axiosPrivate";
import InvoicesSkeleton from "../../components/skeletons/InvoicesSkeleton";
import { useDispatch, useSelector } from "react-redux";
import {
  getInvoiceDetailsData,
  getProfilebyId
} from "../../redux/invoiceDetailsSlice";
import LineSkeleton from "../../components/skeletons/LineSkeleton";
import { CLIENT_DETAILS_PATH } from "../../route/paths";

const Invoicedetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const loading = useSelector(
    (state) => state?.invoiceDetails?.invDetailsDataLoading
  );
  const initialRender = useRef(true);
  const [invData, setInvData] = useState({});
  const [quoteStatus, setQuoteStatus] = useState([]);
  const [legalName, setLegalName] = useState("");
  const [showVoidedModal, setShowVoidedModal] = useState(false);

  const isVoidedInvoice = useMemo(() => {
    return invData?.invoice?.externalInvoiceStatus === INVOICE_STATUS_VOIDED;
  }, [invData?.invoice?.externalInvoiceStatus]);

  useEffect(() => {
    if (isVoidedInvoice) {
      setShowVoidedModal(true);
    }
  }, [isVoidedInvoice]);

  const toggleVoidedInvModal = useCallback(() => {
    setShowVoidedModal((prev) => !prev);
  }, []);

  const fetchInvoiceData = useCallback(async () => {
    try {
      const response = await dispatch(getInvoiceDetailsData({ id })).unwrap();

      setInvData(response?.data?.data);
    } catch (error) {
      showToast(
        error?.response?.data?.errors || responseStatus?.errorText,
        responseStatus?.error
      );
    }
  }, [id, dispatch]);

  useEffect(() => {
    fetchInvoiceData();
  }, [fetchInvoiceData]);

  // Load Quote Status
  const loadQuoteStatus = useCallback(async () => {
    try {
      const response = await privateAxios.get(getQuoteStatusURL);
      setQuoteStatus(response?.data?.data || []);
    } catch (error) {
      showToast(
        error?.response?.data?.errors || responseStatus?.errorText,
        responseStatus?.error
      );
    }
  }, []);

  useEffect(() => {
    if (initialRender.current) {
      loadQuoteStatus();
      initialRender.current = false;
    }
  }, [loadQuoteStatus]);

  // for get legal name from profile
  useEffect(() => {
    if (!invData?.userAccountId) return;
    const getLegalName = async () => {
      try {
        const response = await dispatch(
          getProfilebyId({ userAccountId: invData.userAccountId })
        ).unwrap();
        setLegalName(response?.data?.displayName);
      } catch {
        // handle catch here;
      }
    };
    getLegalName();
  }, [invData?.userAccountId, dispatch]);

  return (
    <>
      <div className="invoice-details-wrap">
        <div className="container-fluid p-0">
          <div className="row">
            <div className="col-12">
              <div className="invoice-header">
                <h2>
                  {loading ? (
                    <LineSkeleton width="120px" height="25px" />
                  ) : (
                    <>
                      {FtNo}
                      {invData?.fmfbFundQuotesId}
                    </>
                  )}
                </h2>
                <h1>
                  <Link
                    to={CLIENT_DETAILS_PATH.CLIENT_DETAILS(
                      invData?.invoice?.userAccountId
                    )}
                  >
                    {legalName}
                  </Link>
                </h1>
              </div>
            </div>
          </div>
          {loading ? (
            <InvoicesSkeleton />
          ) : (
            <div className="row">
              <div className="col-md-6 mb-3">
                <div className="information-section mb-3">
                  <Information
                    invData={invData}
                    quoteStatus={quoteStatus}
                    fetchInvoiceData={fetchInvoiceData}
                  />
                </div>
                <div className="invoice-details-wrap mb-3">
                  <Details
                    invData={invData}
                    fetchInvoiceData={fetchInvoiceData}
                    legalName={legalName}
                  />
                </div>
                <div className="invoice-log-wrap mb-3">
                  <XeroWriteBack invData={invData} />
                </div>
                <div className="invoice-log-wrap mb-3">
                  <Invoicelog invData={invData} />
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <div className="fee-section mb-3">
                  <Fee invData={invData} fetchInvoiceData={fetchInvoiceData} />
                </div>
                <div className="payment-section mb-3">
                  <Payments
                    invData={invData}
                    fetchInvoiceData={fetchInvoiceData}
                  />
                </div>
                <div className="calculator-section mb-3">
                  <FeeCalculator invData={invData} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <VoidedInvoiceInfoModal
        isOpen={showVoidedModal}
        toggle={toggleVoidedInvModal}
      />
    </>
  );
};

export default Invoicedetails;
